/**
 * Applied when a user has no stored locale yet, or the stored value is unsupported.
 * Kept in sync with the backend's `Localization.FallbackCultureName`. The locale is no longer
 * used for date rendering (see {@link formatDateByPattern}); it drives the `Accept-Language`
 * header only.
 */
export const DEFAULT_LOCALE = 'en-US';

/** A selectable locale (used for the `Accept-Language` header, not date display). */
export interface LocaleOption {
  /** BCP-47 culture, e.g. `de-DE`. */
  readonly value: string;
  /** Friendly name shown to the user. */
  readonly label: string;
}

/**
 * The date-format patterns a user can choose. Values mirror the backend's
 * `CountryDateFormat.SupportedFormats` exactly — the API validates against these strings.
 */
export const SUPPORTED_DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (05/21/2026)' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (21.05.2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-05-21)' },
] as const;

export type DateFormatPattern = (typeof SUPPORTED_DATE_FORMATS)[number]['value'];

/** Match the locales to their corresponding date format patterns. */
export const LOCALE_TO_DATE_FORMAT: Record<string, DateFormatPattern> = {
  'en-US': 'MM/DD/YYYY',
  'de-DE': 'DD.MM.YYYY',
  'sv-SE': 'YYYY-MM-DD',
  'fr-FR': 'DD.MM.YYYY',
  'it-IT': 'DD.MM.YYYY',
};

/** Backend default (`TfsDefaults.DateFormat`), used when the stored value is missing/unsupported. */
export const DEFAULT_DATE_FORMAT: DateFormatPattern = 'MM/DD/YYYY';

export function isSupportedDateFormat(
  value: string | null | undefined
): value is DateFormatPattern {
  return !!value && SUPPORTED_DATE_FORMATS.some(format => format.value === value);
}

/** A selectable IANA time zone with a UTC-offset display label. */
export interface TimeZoneOption {
  /** IANA identifier persisted to the backend, e.g. `Europe/Berlin`. */
  readonly value: string;
  /** UTC-offset-prefixed label shown to the user. */
  readonly label: string;
}

/** Backend default (`TfsDefaults.TimeZone`). */
export const DEFAULT_TIME_ZONE = 'Etc/UTC';

/**
 * A curated list of standard UTC time zones. The backend accepts any valid IANA id, so the
 * value is the IANA identifier and the label is the conventional UTC-offset grouping. Offsets
 * are the zones' standard (non-DST) offsets, matching the usual OS-style picker.
 */
export const SUPPORTED_TIME_ZONES: readonly TimeZoneOption[] = [
  { value: 'Etc/GMT+12', label: 'UTC-12:00 - International Date Line West' },
  { value: 'Pacific/Pago_Pago', label: 'UTC-11:00 - American Samoa, Midway Island' },
  { value: 'Pacific/Honolulu', label: 'UTC-10:00 - Hawaii' },
  { value: 'America/Anchorage', label: 'UTC-09:00 - Alaska' },
  { value: 'America/Los_Angeles', label: 'UTC-08:00 - Pacific Time (US & Canada)' },
  { value: 'America/Denver', label: 'UTC-07:00 - Mountain Time (US & Canada)' },
  { value: 'America/Chicago', label: 'UTC-06:00 - Central Time (US & Canada), Mexico City' },
  { value: 'America/New_York', label: 'UTC-05:00 - Eastern Time (US & Canada)' },
  { value: 'America/Halifax', label: 'UTC-04:00 - Atlantic Time (Canada)' },
  { value: 'America/Sao_Paulo', label: 'UTC-03:00 - Brasilia, Buenos Aires' },
  { value: 'Atlantic/South_Georgia', label: 'UTC-02:00 - Mid-Atlantic' },
  { value: 'Atlantic/Azores', label: 'UTC-01:00 - Azores, Cape Verde Is.' },
  { value: 'Etc/UTC', label: 'UTC+00:00 - Coordinated Universal Time' },
  { value: 'Europe/London', label: 'UTC+00:00 - London, Dublin, Lisbon' },
  { value: 'Europe/Berlin', label: 'UTC+01:00 - Amsterdam, Berlin, Rome, Vienna, Zurich' },
  { value: 'Europe/Paris', label: 'UTC+01:00 - Brussels, Madrid, Paris' },
  { value: 'Europe/Warsaw', label: 'UTC+01:00 - Sarajevo, Skopje, Warsaw, Zagreb' },
  { value: 'Europe/Stockholm', label: 'UTC+01:00 - Stockholm, Copenhagen, Oslo' },
  { value: 'Europe/Athens', label: 'UTC+02:00 - Athens, Bucharest, Helsinki' },
  { value: 'Europe/Istanbul', label: 'UTC+03:00 - Istanbul, Moscow, Nairobi' },
  { value: 'Asia/Dubai', label: 'UTC+04:00 - Abu Dhabi, Dubai' },
  { value: 'Asia/Karachi', label: 'UTC+05:00 - Islamabad, Karachi, Tashkent' },
  { value: 'Asia/Kolkata', label: 'UTC+05:30 - Chennai, Kolkata, Mumbai, New Delhi' },
  { value: 'Asia/Dhaka', label: 'UTC+06:00 - Astana, Dhaka' },
  { value: 'Asia/Bangkok', label: 'UTC+07:00 - Bangkok, Hanoi, Jakarta' },
  { value: 'Asia/Shanghai', label: 'UTC+08:00 - Beijing, Hong Kong, Singapore' },
  { value: 'Asia/Tokyo', label: 'UTC+09:00 - Osaka, Sapporo, Tokyo, Seoul' },
  { value: 'Australia/Sydney', label: 'UTC+10:00 - Canberra, Melbourne, Sydney' },
  { value: 'Pacific/Noumea', label: 'UTC+11:00 - New Caledonia, Solomon Is.' },
  { value: 'Pacific/Auckland', label: 'UTC+12:00 - Auckland, Wellington, Fiji' },
];

export interface DateFormatOptions {
  /** Append the 24h time component (`HH:mm`). */
  time?: boolean;
  /** IANA time zone the value is rendered in, e.g. `Europe/Berlin`. Defaults to the runtime zone. */
  timeZone?: string;
}

/**
 * Renders a date using an explicit pattern (`MM/DD/YYYY`, `DD.MM.YYYY`, `YYYY-MM-DD`), independent
 * of locale. The year/month/day (and, in time mode, the 24h `HH:mm`) parts are resolved in the
 * given `timeZone` and then assembled per the chosen pattern. Unknown patterns fall back to
 * {@link DEFAULT_DATE_FORMAT}.
 */
export function formatDateByPattern(
  value: Date | string | number | null | undefined,
  dateFormat: string | null | undefined,
  options?: DateFormatOptions
): string | null {
  if (value == null || value === '') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const pattern = isSupportedDateFormat(dateFormat) ? dateFormat : DEFAULT_DATE_FORMAT;
  const parts = resolveParts(date, options);

  let datePart: string;
  switch (pattern) {
    case 'DD.MM.YYYY':
      datePart = `${parts.day}.${parts.month}.${parts.year}`;
      break;
    case 'YYYY-MM-DD':
      datePart = `${parts.year}-${parts.month}-${parts.day}`;
      break;
    default:
      datePart = `${parts.month}/${parts.day}/${parts.year}`;
  }

  return options?.time ? `${datePart} ${parts.hour}:${parts.minute}` : datePart;
}

type DateField = 'year' | 'month' | 'day';

/** Separator + field order per supported pattern — the inverse of the `switch` above. */
const PATTERN_LAYOUT: Record<
  DateFormatPattern,
  { separator: string; fields: readonly [DateField, DateField, DateField] }
> = {
  'MM/DD/YYYY': { separator: '/', fields: ['month', 'day', 'year'] },
  'DD.MM.YYYY': { separator: '.', fields: ['day', 'month', 'year'] },
  'YYYY-MM-DD': { separator: '-', fields: ['year', 'month', 'day'] },
};

/**
 * Parses a string typed in the given pattern into a local-midnight `Date`, the inverse of
 * {@link formatDateByPattern}. Unknown/missing patterns fall back to {@link DEFAULT_DATE_FORMAT},
 * exactly like `formatDateByPattern`.
 *
 * Designed to be called on every keystroke of a live date input (a `DateAdapter.parse` override
 * is invoked on each `input` event, not just on blur/submit):
 * - A string that is not yet a syntactically complete date for the pattern (wrong separator/part
 *   count, a field with the wrong digit count, non-numeric characters) returns `null` — Angular
 *   Material's own "not invalid yet" check treats a falsy parse result this way, so the field
 *   stays quiet mid-typing instead of flashing a parse error on every keystroke.
 * - A string with three syntactically well-formed numeric fields that nonetheless denotes an
 *   impossible calendar date (month 13, day 32, 31 April, 29 February in a non-leap year) returns
 *   an *invalid* `Date` (`new Date(NaN)`), which Material surfaces as a parse error — the user has
 *   finished typing something wrong, so it should be flagged.
 */
export function parseDateByPattern(
  value: string | null | undefined,
  dateFormat: string | null | undefined
): Date | null {
  if (!value) {
    return null;
  }

  const pattern = isSupportedDateFormat(dateFormat) ? dateFormat : DEFAULT_DATE_FORMAT;
  const { separator, fields } = PATTERN_LAYOUT[pattern];

  const rawParts = value.trim().split(separator);
  if (rawParts.length !== 3) {
    // Missing separators (still typing) or a separator that doesn't belong to this pattern — in
    // both cases we can't yet tell this apart from "not done typing".
    return null;
  }

  const parsed: Partial<Record<DateField, number>> = {};
  for (let i = 0; i < 3; i++) {
    const part = rawParts[i];
    const field = fields[i];
    const expectedLength = field === 'year' ? 4 : undefined;
    const isNumeric = /^\d+$/.test(part);
    const lengthOk = expectedLength
      ? part.length === expectedLength
      : part.length >= 1 && part.length <= 2;
    if (!isNumeric || !lengthOk) {
      return null;
    }
    parsed[field] = Number(part);
  }

  const { year, month, day } = parsed as Record<DateField, number>;
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return new Date(NaN);
  }

  // Mirror the platform's local-midnight construction convention: `setFullYear` (not the `Date`
  // constructor) avoids the legacy 2-digit-year -> 1900s special case.
  const date = new Date();
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  // `setFullYear` silently overflows (e.g. day 32 rolls into the next month) instead of throwing
  // — detect and reject that instead of returning a rolled-over-but-"valid" date.
  const overflowed =
    date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day;
  return overflowed ? new Date(NaN) : date;
}

interface DateParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
}

function resolveParts(date: Date, options?: DateFormatOptions): DateParts {
  const intlOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(options?.timeZone ? { timeZone: options.timeZone } : {}),
    ...(options?.time ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } : {}),
  };
  // `en-US` is a stable locale for extracting numeric parts; the pattern controls the final order.
  const formatted = formatParts(date, intlOptions);
  const lookup = (type: Intl.DateTimeFormatPartTypes) =>
    formatted.find(part => part.type === type)?.value ?? '';
  return {
    year: lookup('year'),
    month: lookup('month'),
    day: lookup('day'),
    hour: lookup('hour'),
    minute: lookup('minute'),
  };
}

function formatParts(
  date: Date,
  intlOptions: Intl.DateTimeFormatOptions
): Intl.DateTimeFormatPart[] {
  try {
    return new Intl.DateTimeFormat('en-US', intlOptions).formatToParts(date);
  } catch {
    // An invalid `timeZone` makes the constructor throw — fall back to the runtime zone.
    const fallback = { ...intlOptions };
    delete fallback.timeZone;
    return new Intl.DateTimeFormat('en-US', fallback).formatToParts(date);
  }
}
